// Tiny fetch wrapper that talks to the Django backend.

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

export class ApiError extends Error {
  constructor(public status: number, public data: unknown) {
    super(`API ${status}`);
    this.name = "ApiError";
  }
}

function getAccessToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("matchrite.access");
}

export function setTokens(access: string, refresh: string) {
  localStorage.setItem("matchrite.access", access);
  localStorage.setItem("matchrite.refresh", refresh);
}

export function storeRole(role: string) {
  localStorage.setItem("matchrite.role", role);
}

export function getStoredRole(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("matchrite.role");
}

export function clearTokens() {
  localStorage.removeItem("matchrite.access");
  localStorage.removeItem("matchrite.refresh");
  localStorage.removeItem("matchrite.role");
}

/** Trekt het refresh-token serverkant in en wist de lokale opslag. */
export async function logout() {
  const refresh = typeof window !== "undefined"
    ? localStorage.getItem("matchrite.refresh")
    : null;
  if (refresh) {
    try {
      await fetch(`${API_URL}/api/auth/logout/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
    } catch {
      // Netwerkfout mag het uitloggen in de browser niet blokkeren.
    }
  }
  clearTokens();
}

// Singleton promise so concurrent 401s share one refresh call.
let refreshPromise: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const refresh = typeof window !== "undefined"
      ? localStorage.getItem("matchrite.refresh")
      : null;
    if (!refresh) return null;
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      localStorage.setItem("matchrite.access", data.access);
      // De backend roteert: het gebruikte refresh-token is nu ingetrokken, dus
      // het nieuwe moet hier landen — anders werkt de volgende refresh niet.
      if (data.refresh) localStorage.setItem("matchrite.refresh", data.refresh);
      return data.access as string;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function fetchWithAuth(path: string, init: RequestInit, token: string | null) {
  return fetch(`${API_URL}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getAccessToken();
  let res = await fetchWithAuth(path, init, token);

  if (res.status === 401 && !path.includes("/auth/refresh/")) {
    const newToken = await attemptRefresh();
    if (newToken) {
      res = await fetchWithAuth(path, init, newToken);
    } else {
      clearTokens();
      if (typeof window !== "undefined") window.location.href = "/login";
      throw new ApiError(401, null);
    }
  }

  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, data);
  }
  return data as T;
}

export type CurrentUser = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: "admin" | "opdrachtgever" | "monteur" | "ep_centraal";
  organization: number | null;
  org_logo_slug: string;
};

export async function fetchMe(): Promise<CurrentUser> {
  return api<CurrentUser>("/api/auth/me/");
}

export async function patchMe(
  patch: Partial<Pick<CurrentUser, "first_name" | "last_name">>,
): Promise<CurrentUser> {
  return api<CurrentUser>("/api/auth/me/", {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export async function changePassword(
  old_password: string,
  new_password: string,
) {
  return api("/api/auth/password/", {
    method: "POST",
    body: JSON.stringify({ old_password, new_password }),
  });
}

export async function login(email: string, password: string) {
  const data = await api<{ access: string; refresh: string }>(
    "/api/auth/login/",
    { method: "POST", body: JSON.stringify({ email, password }) },
  );
  setTokens(data.access, data.refresh);
  return data;
}

export async function signup(payload: {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: "opdrachtgever" | "monteur";
}) {
  return api("/api/auth/signup/", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
